import { PlaybackConfig, Player, PlayerEvent, StyleConfig } from 'bitmovin-player';
import SMILHandler from './SMILHandler';
import browser from './browser';
import { EVENT } from '../contexts/MixpanelConstants';
import f from './formatter2';
import { playerTypes } from '../config/constants';
import { getItemProperties } from '../contexts/MixpanelHelper';
import { updateYouboraAnalytics, setYBPluginOptions } from './youbora';
import h from './common';
import cdnHandler from './CDNHandler';
import playerService from '../helpers/bitmovinPlayer';
import { guardPlayerApi, isPlayerAlive } from './playerGuards';
import { log } from '@intigral/utils';

interface LoadPlayerOptions {
    onStart?: () => void;
    onError?: (error: Error) => void;
    onDestroy?: () => void;
    onFinish?: () => void;
    liveStream?: { clipDurationInSec: number } | boolean;
}

interface SetupPlayerOptions extends LoadPlayerOptions {
    mixpanelEvent?: (event: string, properties: object) => void;
    isMuted?: boolean;
}

class SimpleBitmovinPlayer {
    player: InstanceType<typeof Player> | null = null;
    currentlyPlaying: string | null = null; // flag to abort loading player if slide is changed
    isCarouselTrailerPlaying = false; // flag to check if a carousel trailer playing the promo trailer will not play

    /**
     * Set the currently playing item ID
     * @param id - The ID of the currently playing item
     */
    setCurrentlyPlaying(id: string) {
        this.currentlyPlaying = id;
    }

    /**
     * Get the width and height of an element maintaining aspect ratio
     * @param element - The HTML element to get dimensions from
     * @returns An object containing width and height
     */
    getWidthnHeight(element: HTMLElement) {
        const width = element.offsetWidth;
        const height = element.offsetHeight;
        const ratioedWidth = height * 1.7778;
        const ratioedHeight = width / 1.7778;
        return {
            width: ratioedWidth > width ? ratioedWidth : width,
            height: ratioedHeight > height ? ratioedHeight : height,
        };
    }

    /**
     * Load the player with the given source
     * @param player - The player instance
     * @param itemId - The ID of the item to play
     * @param source - The source URL
     * @param options - Options for start and error callbacks
     */
    loadPlayer(player: InstanceType<typeof Player>, itemId: string, source: string, options: LoadPlayerOptions) {
        const { onStart, onError, onFinish, liveStream } = options;

        if (onStart && typeof onStart === 'function') {
            onStart();
        }

        SMILHandler.resolveUrl(source)
            .then((streamUrl) => {
                if (this.currentlyPlaying !== itemId) {
                    player.destroy();
                    return;
                }
                if (streamUrl.includes('http:')) {
                    streamUrl = streamUrl.replace('http:', 'https:');
                }
                const playerSource = browser.isSafari()
                    ? { hls: streamUrl /* drm: playerService.getSafariDRMConfig() */ }
                    : { dash: streamUrl /* drm: playerService.getOtherDRMConfig() */ };
                return player.load(playerSource);
            })
            .then(() => {
                if (this.currentlyPlaying !== itemId) {
                    player.destroy();
                    return;
                }
                this.player = player;
                cdnHandler.init(null);
                cdnHandler.attachPlayerToCDNSession(player);

                if (liveStream && typeof liveStream !== 'boolean') {
                    const secIntoMs = Number(liveStream.clipDurationInSec) * 1000;
                    setTimeout(() => {
                        this.destroyPlayer();
                        if (onFinish) onFinish();
                    }, secIntoMs);
                }
            })
            .catch((e) => {
                if (onError && typeof onError === 'function') {
                    onError(e);
                }
                this.destroyPlayer();
            });
    }

    /**
     * Setup the player with the given options
     * @param elementRef - The reference to the HTML element
     * @param playerKey - The player key
     * @param source - The source URL
     * @param itemId - The ID of the item to play
     * @param item - The item object
     * @param options - Options for player setup
     */
    setupPlayer(
        elementRef: HTMLElement,
        playerKey: string,
        source: string,
        itemId: string,
        item: any,
        options: SetupPlayerOptions
    ) {
        const { onStart, onDestroy, onError, onFinish, mixpanelEvent, isMuted = true, liveStream } = options;
        const { width, height } = this.getWidthnHeight(elementRef.parentNode as HTMLElement);
        this.destroyPlayer();
        if (source.includes('http:')) {
            source = source.replace('http:', 'https:');
        }
        const playerInstance = guardPlayerApi(
            new Player(elementRef, {
                key: playerKey,
                ui: false,
                style: {
                    controls: false,
                    theme: false,
                    width: `${width}px`,
                    height: `${height}px`,
                    aspectRatio: '16:9',
                } as StyleConfig,
                playback: {
                    autoplay: true,
                    muted: isMuted,
                    aspectRatio: '16:9',
                } as PlaybackConfig,
                events: {
                    [PlayerEvent.PlaybackFinished]: onFinish,
                    [PlayerEvent.TimeChanged]: () => {
                        if (this.currentlyPlaying !== itemId) {
                            playerInstance.destroy();
                        }
                    },
                    [PlayerEvent.Ready]: (e) => {
                        mixpanelEvent?.(`VOD Player - ${EVENT.VOD_PLAYER.START}`, {
                            ...getItemProperties(item),
                            'VOD Duration': f.converToHHMMSS(playerInstance?.getDuration()),
                            'Video Type': liveStream ? playerTypes.LiveTV : playerTypes.Trailer,
                        });
                        setYBPluginOptions(this.player);
                        updateYouboraAnalytics(
                            item,
                            source,
                            liveStream ? playerTypes.LiveTV : playerTypes.Trailer,
                            null
                        );
                    },
                    [PlayerEvent.Error]: (e) => {
                        if (onError) onError(new Error((e as any).message));
                    },
                    [PlayerEvent.Destroy]: () => {
                        if (onDestroy) onDestroy();
                    },
                },
                adaptation: {
                    desktop: {
                        preload: false,
                    },
                    mobile: {
                        preload: false,
                    },
                },
                analytics: false,
            })
        );
        this.loadPlayer(playerInstance, itemId, source, { onStart, onError, onFinish, liveStream });
    }

    /**
     * Destroy the player instance
     */
    destroyPlayer() {
        const player = this.player;
        if (!player) return;

        this.player = null;
        // Stop the CDN session while the player is alive; SmartLib needs `player.off(...)` to work.
        cdnHandler.stopCDNSessionFor(player);
        if (isPlayerAlive(player)) {
            player.destroy().catch((e) => log.debug('[SimpleBitmovinPlayer] destroy failed:', e));
        }
    }
}

export default new SimpleBitmovinPlayer();
